import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { respondApi } from '../services/api';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Calendar, Clock, Check, Globe } from 'lucide-react';

interface EventData {
    id: string;
    name: string;
    comments?: string;
    creator_name: string;
    creator_email: string;
    duration_minutes: number;
    time_slots: {
        id: string;
        start_time_utc: string;
        end_time_utc: string;
    }[];
}

// Common timezones
const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'UTC', label: 'UTC' },
];

export function RespondPage() {
    const { token } = useParams<{ token: string }>();
    const [event, setEvent] = useState<EventData | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
    const [timezone, setTimezone] = useState('America/New_York');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Try to detect user's timezone
        try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (TIMEZONES.some(tz => tz.value === detected)) {
                setTimezone(detected);
            }
        } catch {
            // Keep default
        }
    }, []);

    useEffect(() => {
        if (!token) return;
        loadEvent();
    }, [token]);

    const loadEvent = async () => {
        try {
            const data = await respondApi.getEvent(token!);
            setEvent(data?.event);
            if (data?.recipient?.selected_slot_ids) {
                setSelectedSlots(new Set(data.recipient.selected_slot_ids));
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load event');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSlot = (slotId: string) => {
        setSelectedSlots(prev => {
            const next = new Set(prev);
            if (next.has(slotId)) {
                next.delete(slotId);
            } else {
                next.add(slotId);
            }
            return next;
        });
        setSuccess(false);
    };

    const handleSubmit = async () => {
        if (!token) return;

        setIsSubmitting(true);
        try {
            await respondApi.submit(token, Array.from(selectedSlots));
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to submit response');
        } finally {
            setIsSubmitting(false);
        }
    };


    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh'
            }}>
                <div className="spinner" />
            </div>
        );
    }

    if (error && !event) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: 'var(--space-5)'
            }}>
                <div className="card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                    <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                        Unable to Load Event
                    </h2>
                    <p style={{ color: 'var(--color-gray-500)' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (!event) return null;

    // Group slots by date
    const slotsByDate = event.time_slots.reduce((acc, slot) => {
        const date = formatInTimeZone(parseISO(slot.start_time_utc), timezone, 'yyyy-MM-dd');
        if (!acc[date]) acc[date] = [];
        acc[date].push(slot);
        return acc;
    }, {} as Record<string, typeof event.time_slots>);

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0A2F5E 0%, #1A3A52 50%)',
            paddingBottom: 'var(--space-8)'
        }}>
            {/* Header */}
            <div style={{
                background: 'rgba(255,255,255,0.1)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-6)'
            }}>
                <div className="container" style={{ maxWidth: '600px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                        <Calendar size={24} />
                        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            AAM <span style={{ color: 'var(--color-primary)' }}>Let's Meet</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container" style={{ maxWidth: '600px' }}>
                <div className="card">
                    <h1 style={{ marginBottom: 'var(--space-2)' }}>{event.name}</h1>

                    <p style={{
                        color: 'var(--color-gray-500)',
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        From <strong>{event.creator_name}</strong>
                    </p>

                    {event.comments && (
                        <div style={{
                            background: 'var(--color-gray-100)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-5)'
                        }}>
                            {event.comments}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-5)',
                        color: 'var(--color-gray-500)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={16} />
                            {event.duration_minutes} minutes
                        </span>
                    </div>

                    {/* Timezone Selector */}
                    <div style={{ marginBottom: 'var(--space-5)' }}>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Globe size={14} />
                            Your Timezone
                        </label>
                        <select
                            className="form-select"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Success Message */}
                    {success && (
                        <div className="alert alert-success" style={{ marginBottom: 'var(--space-4)' }}>
                            <Check size={18} style={{ display: 'inline', marginRight: '8px' }} />
                            Your response has been recorded!
                        </div>
                    )}

                    {/* Time Slots */}
                    <div>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>
                            Select the times that work for you
                        </h3>

                        {Object.entries(slotsByDate).map(([date, slots]) => (
                            <div key={date} style={{ marginBottom: 'var(--space-5)' }}>
                                <h4 style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-gray-500)',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    {formatInTimeZone(parseISO(date), timezone, 'EEEE, MMMM d')}
                                </h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                    {slots.map(slot => {
                                        const isSelected = selectedSlots.has(slot.id);
                                        const startTime = formatInTimeZone(parseISO(slot.start_time_utc), timezone, 'h:mm a');
                                        const endTime = formatInTimeZone(parseISO(slot.end_time_utc), timezone, 'h:mm a');

                                        return (
                                            <button
                                                key={slot.id}
                                                onClick={() => toggleSlot(slot.id)}
                                                style={{
                                                    padding: 'var(--space-3) var(--space-4)',
                                                    border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-gray-300)',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: isSelected ? 'var(--color-primary)' : 'white',
                                                    color: isSelected ? 'white' : 'var(--color-gray-700)',
                                                    cursor: 'pointer',
                                                    transition: 'all var(--transition-fast)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {isSelected && <Check size={16} />}
                                                {startTime} - {endTime}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Submit Button */}
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="btn btn-primary btn-lg"
                        style={{ width: '100%', marginTop: 'var(--space-4)' }}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="spinner" style={{ width: '18px', height: '18px' }} />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Check size={18} />
                                Submit Response ({selectedSlots.size} selected)
                            </>
                        )}
                    </button>

                    <p style={{
                        textAlign: 'center',
                        marginTop: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-gray-500)'
                    }}>
                        You can return to this page and update your selections anytime
                    </p>
                </div>
            </div>
        </div>
    );
}
